import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IOptionBase, ISelectInnerProps } from './models';
import { classnames } from '../../utils/classnames';
import { DEFAULT_COMPONENTS, DRAG_AND_DROP_COMPONENTS } from './consts';
import { BreakpointsContext } from '../../providers/Breakpoints';
import { AnyType } from '@src/models/common';
import { arrayMove } from 'react-sortable-hoc';
import { SelectSortableContainer as ReactSelect } from './_SortableContainer/SelectSortableContainer';
import { PropsValue } from 'react-select';
import { groupOptions } from './utils';
import { FormatGroupLabel } from './_FormatGroupLabel';
import { getClasses } from '@src/utils/getClass';
import { SelectContext, SelectProvider } from './context';
import { InputIcon } from '../InputIcon';
import styled from './Select.module.scss';
import { Box } from '../Box';

/** Компонент выбора */
export const SelectComponent: FC<ISelectInnerProps> = ({
  id,
  size = 'md',
  className,
  isIndicator = true,
  label,
  menuIsOpen: propsMenuIsOpen,
  options,
  isDragAndDrop,
  isGroupedOptions,
  errorMessage,
  helpMessage,
  isHelpMessage,
  preIcon,
  postIcon,
  isClearable,
  placeholder = '',
  onChange = () => {},
  onFocus,
  onBlur,
  noOptionsMessage = () => null,
  noSelectedOptions,
  staticLabel,
  setMenuMaxWidth,
  isSearchable = true,
  ...rest
}) => {
  const { isMobile } = useContext(BreakpointsContext);

  const [menuPortalTarget, setMenuPortalTarget] = useState<HTMLElement | null>(null);
  const [menuIsOpen, setMenuIsOpen] = useState(propsMenuIsOpen || undefined);
  const [selected, setSelected] = useState<AnyType>(rest.value || []);
  const [isFocus, setIsFocus] = useState(false);
  const { setControlElement } = useContext(SelectContext);

  useEffect(() => {
    setSelected(rest.value || []);
  }, [rest.value]);

  const handleChange = useCallback(
    (v, a) => {
      setSelected(v);
      onChange?.(v, { action: isDragAndDrop ? 'change-sort' : a } as AnyType);
      if (!rest.isMulti) {
        setMenuIsOpen(undefined);
      }
    },
    [onChange, rest.isMulti, isDragAndDrop],
  );

  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }) => {
      const newValue: PropsValue<IOptionBase> = arrayMove(selected, oldIndex, newIndex);
      setSelected(newValue);
      onChange?.(newValue, { action: 'change-sort' } as AnyType);
    },
    [selected, onChange],
  );

  const handleFocus = useCallback(
    (e) => {
      setMenuIsOpen(true);
      setIsFocus(true);
      onFocus?.(e);
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (e) => {
      setIsFocus(false);
      onBlur?.(e);
    },
    [onBlur],
  );

  useEffect(() => {
    setMenuPortalTarget(rest.menuPortalTarget || document.body);
  }, [rest.menuPortalTarget]);

  const props = useMemo(() => {
    return isDragAndDrop
      ? {
          components: DRAG_AND_DROP_COMPONENTS,
          axis: 'xy',
          onSortEnd,
          distance: 4,
        }
      : {
          components: DEFAULT_COMPONENTS,
        };
  }, [isDragAndDrop, onSortEnd]);

  const groupedProps = useMemo(() => {
    return isGroupedOptions
      ? {
          options: groupOptions(options),
          formatGroupLabel: FormatGroupLabel,
        }
      : {
          options: options,
        };
  }, [isGroupedOptions, options]);

  const isValue = Boolean(Array.isArray(selected) ? selected.length : selected);
  const isSmallLabel = isValue || isFocus;
  const isPlaceholder = Boolean(!label || isSmallLabel);

  const rootClasses = classnames(styled.root, ...getClasses(styled, { size }), {
    [styled.error]: Boolean(errorMessage),
    [styled.iconPre]: Boolean(preIcon),
    [styled.iconPost]: Boolean(postIcon || isClearable || isIndicator),
  });
  const containerClasses = classnames(className, styled.container);

  return (
    <div className={rootClasses} data-id={id}>
      <div
        ref={setControlElement}
        className={containerClasses}
        data-size={size}
        data-elem="container"
        data-multi={Boolean(rest.isMulti && isValue)}
      >
        {!staticLabel ? (
          <div data-elem="label" data-size={size} className={styled.label} data-label-small={isSmallLabel}>
            {label}
          </div>
        ) : (
          selected.length > 0 && <Box className={styled.counter} />
        )}

        {preIcon && <InputIcon config={preIcon} data-elem="preIcon" data-size={size} size="md-light" />}

        <ReactSelect
          {...rest}
          {...props}
          {...groupedProps}
          isSearchable={!isMobile && isSearchable}
          //Не используем id т.к. он будет отображаться в DOM элементе
          data-id={id}
          placeholder={isPlaceholder ? placeholder : ''}
          className={styled.selectContainer}
          //@ts-ignore
          isIndicator={isIndicator}
          isClearable={!postIcon && isClearable}
          hideSelectedOptions={false}
          closeMenuOnSelect={isMobile || rest.isMulti ? false : undefined}
          value={selected}
          menuIsOpen={isMobile ? menuIsOpen : propsMenuIsOpen}
          menuPortalTarget={menuPortalTarget}
          setMenuIsOpen={setMenuIsOpen}
          onFocus={handleFocus}
          onChange={handleChange as AnyType}
          noOptionsMessage={noOptionsMessage}
          onBlur={handleBlur}
          noSelectedOptions={noSelectedOptions}
          staticLabel={staticLabel}
          setMenuMaxWidth={setMenuMaxWidth}
        />

        {postIcon && (
          <InputIcon
            config={postIcon}
            data-elem="postIcon"
            data-indicator={isIndicator}
            data-size={size}
            size="md-light"
          />
        )}
      </div>

      {isHelpMessage && (
        <>
          {errorMessage ? (
            <div data-elem="message" data-id="errorMsg">
              {errorMessage}
            </div>
          ) : (
            <div data-elem="message" data-id="helpMsg">
              {helpMessage}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const Select: FC<ISelectInnerProps> = ({ getValue, ...props }) => (
  <SelectProvider
    getValue={getValue}
    postIcon={props.postIcon}
    size={props.size}
    isGroupedOptions={props.isGroupedOptions}
  >
    <SelectComponent {...props} />
  </SelectProvider>
);
